import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProductInfo, Paragraph, Button, Layout, ProductTable, DescriptionList } from '@vwfs-bronson/bronson-react';
import { DefinitionListHorizontal, Notification, NotificationStatus } from '@cp-shared-15/frontend-ui';
import { VehicleImageServiceResponse, VehicleImageView } from '@cp-shared-15/apis';
import { ContractDataWithLinks, EarlySettlementData, FinancialDetails } from 'cpie-common';
import {
    getEarlySettlementContractDetailsListItems,
    getEarlySettlementEnquiryDetailsListItems,
    getEarlySettlementFinancialDetailsListItems,
    getEarlySettlementTotalList,
} from '../util';
import { dashboardPagePath } from '../../navigation/paths';
import { getDefaultVehicleImageByBrand } from '../../../utils/vehicle-image-utils';
import { DealerCallback } from '../dealer-callback/DealerCallback';

export const EarlySettlementFigureUi: React.FC<{
    contract?: ContractDataWithLinks;
    financialDetails?: FinancialDetails;
    vehicleImages?: VehicleImageServiceResponse;
    earlySettlementData?: EarlySettlementData;
    showDefaultImage?: boolean;
}> = ({ contract, financialDetails, vehicleImages, earlySettlementData, showDefaultImage }) => {
    const { t } = useTranslation('early-settlement');
    const history = useHistory();

    if (!contract || !financialDetails || !earlySettlementData) {
        return null;
    }
    const allowDealerCallback = true;
    const title = `${contract.brand ?? ''} ${contract.model ?? ''}`;
    const titleTagline = contract.contractNumber;

    const contractInfoList = getEarlySettlementContractDetailsListItems(t, contract, financialDetails);
    const enquiryInfoList = getEarlySettlementEnquiryDetailsListItems(t, earlySettlementData);
    const financialInfoList = getEarlySettlementFinancialDetailsListItems(t, earlySettlementData);
    const totalSettlementList = getEarlySettlementTotalList(t, earlySettlementData);

    const getImageByView = (view: VehicleImageView) => {
        return vehicleImages?.images?.find(image => image.view === view);
    };

    const getImageSrc = () => {
        const frontRightImage = getImageByView(VehicleImageView.EXTERIOR_FRONT_RIGHT);
        const frontLeftImage = getImageByView(VehicleImageView.EXTERIOR_FRONT_LEFT);

        const image = frontRightImage ?? frontLeftImage;
        if (image) {
            return image.url;
        } else if (showDefaultImage) {
            return getDefaultVehicleImageByBrand(contract.brand);
        }
        return '';
    };

    const goBackToDashboard = () => history.push(dashboardPagePath());

    const onContactButtonClick = () => {
        window.open(t('notification.button-link'), '_blank');
    };

    return (
        <Layout>
            <Layout.Item>
                <ProductInfo title={title} titleTagline={titleTagline} media={<img src={getImageSrc()} alt="" />}>
                    <DescriptionList testId="early-settlement-contract-info-list" split>
                        {contractInfoList.map((item, index) => {
                            return (
                                <DescriptionList.Group key={index} termText={item.label}>
                                    <DescriptionList.Detail>{item.value}</DescriptionList.Detail>
                                </DescriptionList.Group>
                            );
                        })}
                    </DescriptionList>
                </ProductInfo>
            </Layout.Item>

            <Layout.Item>
                <Paragraph>{t('info-text-1')}</Paragraph>
                <Paragraph>{t('info-text-2')}</Paragraph>
            </Layout.Item>

            <Layout.Item default="1/2" m="1/1">
                <ProductTable>
                    <ProductTable.Section>
                        <DefinitionListHorizontal testId="early-settlement-enquiry-info-list" list={enquiryInfoList} />
                    </ProductTable.Section>
                </ProductTable>
                <Paragraph className="u-mt-small u-font-size-fs-1">
                    {t('enquiry.valid-until-date-disclaimer')}
                </Paragraph>
            </Layout.Item>

            <Layout.Item default="1/2" m="1/1">
                <ProductTable
                    summary={
                        <DefinitionListHorizontal
                            testId="early-settlement-total-info-list"
                            list={totalSettlementList}
                        />
                    }
                >
                    <ProductTable.Section>
                        <DefinitionListHorizontal
                            testId="early-settlement-financial-info-list"
                            list={financialInfoList}
                        />
                    </ProductTable.Section>
                </ProductTable>
            </Layout.Item>

            {contract.contractNumber && contract.encryptedContractNumber && earlySettlementData && (
                <Layout.Item>
                    {allowDealerCallback ? (
                        <DealerCallback
                            contractNumber={contract.contractNumber}
                            encryptedContractNumber={contract.encryptedContractNumber}
                            earlySettlementData={earlySettlementData}
                        />
                    ) : (
                        <Notification
                            status={NotificationStatus.info}
                            headline={t('notification.headline')}
                            buttons={[
                                <Button
                                    secondary
                                    key="contact-us-button"
                                    id="contact-us-button"
                                    onClick={onContactButtonClick}
                                    role="button"
                                >
                                    {t('notification.button-text')}
                                </Button>,
                            ]}
                        >
                            <span className="u-mr-small">{t('notification.pre-button-text')}</span>
                        </Notification>
                    )}
                </Layout.Item>
            )}

            <Layout.Item>
                <Button secondary testId="back-button" onClick={goBackToDashboard} icon="semantic-back" iconReversed>
                    {t('back-button')}
                </Button>
            </Layout.Item>
        </Layout>
    );
};
